import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  pickFeaturedIndex,
  QuoteCard,
  QuoteItem,
} from '@/components/quick-study-results/QuickStudyOpenEndCard'

import { StudyDocQuote, studyDocQuoteToItem } from './enrichStudyDocQuote'
import {
  MosaicPickerLookup,
  resolveMosaicPickerImages,
} from './PortraitMosaicStrip/resolveMosaicPickerImages'
import { StudyDocQuotesPrintDigest } from './StudyDocPrintDigest'
import { StudyDocQuotesSlider } from './StudyDocQuotesSlider'

import './studyDocQuotes.css'

type StudyDocQuotesView = 'slider' | 'all'

interface StudyDocQuoteEntry {
  id: string
  quote: StudyDocQuote
}

interface StudyDocQuotesGalleryProps {
  entries: Array<StudyDocQuoteEntry>
  /** Slim picker population for quote avatars missing imageUrl. Reuses mosaic resolver. */
  populationId?: string
}

function missingPortraitLookups(entries: Array<StudyDocQuoteEntry>): Array<MosaicPickerLookup> {
  const lookups: Array<MosaicPickerLookup> = []
  const seen = new Set<string>()

  for (const entry of entries) {
    const personaId = (entry.quote.personaId || '').trim()
    if (!personaId || seen.has(personaId)) {
      continue
    }
    if ((entry.quote.imageUrl || '').trim()) {
      continue
    }
    seen.add(personaId)
    lookups.push({
      personaId,
      name: (entry.quote.name || entry.quote.attribution || '').trim(),
    })
  }

  return lookups
}

export function StudyDocQuotesGallery(props: StudyDocQuotesGalleryProps) {
  const { entries, populationId } = props

  const { t } = useTranslation('app')
  const [view, setView] = useState<StudyDocQuotesView>('slider')
  const [fetchedPickerImages, setFetchedPickerImages] = useState<Record<string, string>>({})

  const missingLookups = useMemo(() => missingPortraitLookups(entries), [entries])
  const missingPickerKey = useMemo(
    () =>
      missingLookups
        .map((lookup) => lookup.personaId)
        .sort()
        .join(','),
    [missingLookups],
  )

  useEffect(() => {
    if (!populationId || missingLookups.length === 0) {
      setFetchedPickerImages({})
      return
    }

    let cancelled = false

    void resolveMosaicPickerImages(populationId, missingLookups).then((next) => {
      if (!cancelled) {
        setFetchedPickerImages(next)
      }
    })

    return () => {
      cancelled = true
    }
    // Keyed on `missingPickerKey` (stable sorted ids) rather than `missingLookups`
    // identity — same pattern as PortraitMosaicStrip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populationId, missingPickerKey])

  const items = useMemo(
    () =>
      entries.map((entry) => {
        const item = studyDocQuoteToItem(entry.quote, entry.id)
        if (item.imageUrl) {
          return item
        }
        const personaId = (entry.quote.personaId || '').trim()
        const pickerUrl = personaId ? fetchedPickerImages[personaId] : undefined
        if (!pickerUrl) {
          return item
        }
        return { ...item, imageUrl: pickerUrl }
      }),
    [entries, fetchedPickerImages],
  )
  const featuredIndex = useMemo(() => pickFeaturedIndex(items), [items])
  const orderedItems = useMemo(() => {
    if (featuredIndex < 0) {
      return items
    }
    return [items[featuredIndex], ...items.filter((_, index) => index !== featuredIndex)]
  }, [items, featuredIndex])
  const hasToggle = orderedItems.length > 1

  return (
    <div className="space-y-4" data-testid="study-doc-quotes-gallery">
      <div
        className="space-y-3 print:hidden"
        data-print-hide=""
        data-testid="study-doc-quotes-gallery-live"
        data-view={view}
      >
        {hasToggle ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setView((current) => (current === 'slider' ? 'all' : 'slider'))}
              aria-pressed={view === 'all'}
              className="rounded-full border border-border bg-transparent px-3 py-1 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="study-doc-quote-view-toggle"
            >
              {view === 'slider' ? t('studyDocChrome.showAllQuotes') : t('studyDocChrome.showQuotesAsSlider')}
            </button>
          </div>
        ) : null}
        {view === 'slider' ? (
          <StudyDocQuotesSlider items={orderedItems} />
        ) : (
          <StudyDocQuotesList items={orderedItems} />
        )}
      </div>
      <StudyDocQuotesPrintDigest items={items} featuredIndex={featuredIndex} />
    </div>
  )
}

interface StudyDocQuotesListProps {
  items: Array<QuoteItem>
}

function StudyDocQuotesList(props: StudyDocQuotesListProps) {
  const { items } = props

  return (
    <div className="flex flex-col gap-3" data-testid="study-doc-quotes-list">
      {items.map((item) => (
        <div key={item.key} data-testid="study-doc-quote-embed" data-quote-id={item.key}>
          <QuoteCard item={item} />
        </div>
      ))}
    </div>
  )
}
